import axios from 'axios';

// Create an Axios instance for API endpoints (with '/api' base path)
const API = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL || 'http://localhost'}/api`,
  withCredentials: true,
});

// Create a separate Axios instance for User endpoints (without '/api' prefix)
const USER_API = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL || 'http://localhost'}`,
  withCredentials: true,
});

// ==========================
// Faculty & Grants APIs (using API)
// ==========================
export const getFacultyList = async () => {
  const response = await API.get('/api/faculty/');
  return response.data;
};

export const getGrantsForFaculty = async (page, pageSize, search = '') => {
  // Grants endpoint remains unchanged
  const response = await API.get(`/faculty/me/grants/`, {
    params: { 
      page, 
      page_size: pageSize,
      search,
    },
  });
  return response.data;
};

export const getRelatedFacultyForGrant = async (grantId) => {
  const response = await API.get(`/grants/${grantId}/`);
  return response.data;
};

export const getExternalLink = async (grantId) => {
  const response = await API.get(`/grants/${grantId}/external_link/`);
  return response.data;
};

// ==========================
// User Authentication & Faculty Linking APIs (using USER_API)
// ==========================
export const setScholarURL = async () => {
  const response = await USER_API.get('/users/register/link-user/', {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
};

export const makeUserFacultyLink = async (data) => {
  // data is expected to be an object that may include candidate_id.
  const response = await USER_API.post('/users/register/link-user/', data, {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
};

// ==========================
// Chat APIs (using API)
// ==========================
export const startChatThread = async () => {
  const response = await API.post('/start_chat/');
  return response.data;
};

export const sendChat = async (thread_id, user_message) => {
  const response = await API.post('/send_message/', {
    thread_id,
    message: user_message,
  });
  return response.data;
};

export const endChatThread = async (thread_id) => {
  const response = await API.post('/end_chat/', { thread_id });
  return response.data;
};

export const submitFeedback = async (feedbackContent) => {
  const response = await API.post('/feedback/', { content: feedbackContent });
  return response.data;
};

// ==========================
// Endpoints using the authenticated user's faculty (using API)
// ==========================

// For abstracts, we now use the "me" endpoint
export const getFacultyAbstracts = async () => {
  const response = await API.get(`/faculty/me/abstracts/`);
  return response.data;
};

export const addAbstract = async (abstractData) => {
  const response = await API.post(`/faculty/me/abstracts/add/`, abstractData);
  return response.data;
};

// For deletion and update, we still use the abstract ID (with proper permission checks in the backend)
export const deleteAbstract = async (abstractId) => {
  const response = await API.delete(`/abstracts/${abstractId}/delete/`);
  return response.data;
};

export const updateAbstract = async (abstractId, abstractData) => {
  const response = await API.put(`/abstracts/${abstractId}/update/`, abstractData);
  return response.data;
};

// Interests now use the "me" endpoint
export const addInterest = async (interest) => {
  const response = await API.post(`/faculty/me/interests/`, { interest });
  return response.data;
};

export const getFacultyInterests = async () => {
  const response = await API.get(`/faculty/me/interests/`);
  return response.data;
};

export const deleteInterest = async (interest) => {
  const response = await API.delete(`/faculty/me/interests/`, { data: { interest } });
  return response.data;
};

// Summary endpoint updated to use "me"
export const getFacultySummary = async () => {
  const response = await API.get(`/faculty/me/summary/`);
  return response.data;
};

export const updateFacultySummary = async (summary) => {
  const response = await API.put(`/faculty/me/summary/`, { summary });
  return response.data;
};

export const regenerateFacultySummary = async () => {
  const response = await API.post(`/faculty/me/summary/`);
  return response.data;
};

// Revectorization endpoint updated to use "me"
export const revectorizeFacultyVector = async () => {
  const response = await API.post(`/faculty/me/revectorize/`);
  return response.data;
};

// ==========================
// Add token to headers for API instance requests
// ==========================
API.interceptors.request.use((config) => {
  const accessToken = localStorage.getItem('access');
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// ==========================
// Handle token expiration (refresh)
// ==========================
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Check if this is a 429 rate limit response for token refresh endpoint
    if (error.response && error.response.status === 429 && 
        originalRequest.url && originalRequest.url.includes('/users/token/refresh/')) {
      // We're being rate limited on token refresh - wait before trying again
      console.warn('Token refresh rate limited');
      
      // Don't retry this request
      return Promise.reject(error);
    }
    
    // Only attempt token refresh if it's a 401 and we haven't tried refreshing for this request yet
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Check if we have a refresh token
      const refreshToken = localStorage.getItem('refresh');
      if (!refreshToken) {
        // No refresh token available - just reject
        return Promise.reject(error);
      }
      
      try {
        // Add a small delay to prevent rapid retry cycles
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const refreshResponse = await USER_API.post('/users/token/refresh/', { refresh: refreshToken });
        localStorage.setItem('access', refreshResponse.data.access);
        originalRequest.headers.Authorization = `Bearer ${refreshResponse.data.access}`;
        return API(originalRequest);
      } catch (err) {
        // Only redirect to login for refresh token failures on non-analytics endpoints
        if (!originalRequest.url.includes('/analytics/')) {
          window.location.href = '/login';
        }
        return Promise.reject(err);
      }
    }
    
    return Promise.reject(error);
  }
);

export default API;
